/**
 * Localize Zod's BUILT-IN validation messages.
 *
 * Every schema in the app leans on Zod's own wording for the structural
 * failures — "Invalid URL", "Too small: expected string to have >=1 characters",
 * "Invalid input: expected number, received undefined". Those strings are baked
 * into Zod and never passed through i18n, so an Arabic operator got an English
 * sentence under an Arabic label (the Google-Maps field is the visible example,
 * but every form in the app had it).
 *
 * Zod 4 ships translations for these. Rather than pinning one locale at startup
 * — the language is switchable at runtime and no page reload follows — this
 * installs a global error map that picks the translation AT PARSE TIME from the
 * same persisted locale `i18n` reads. Switching language re-renders the form and
 * the next validation pass speaks the new language, with no re-registration.
 *
 * Scope: only messages Zod generates itself. A schema that passes its own
 * `error`/`message` always wins over the locale, which is why our custom config
 * messages go through `vt(...)` instead (see `validation-message.ts`).
 *
 * Called once from the app entry, before the first form is rendered.
 */

import { z } from "zod";
import { getLocale } from "@/shared/lib/i18n";

type LocaleError = NonNullable<z.core.$ZodConfig["localeError"]>;

const LOCALE_ERRORS: Record<string, LocaleError> = {
	ar: z.locales.ar().localeError as LocaleError,
	en: z.locales.en().localeError as LocaleError,
};

/**
 * Resolve the active locale's Zod translation, falling back to English for any
 * installed language Zod has no bundled locale for.
 */
function activeLocaleError(): LocaleError {
	return LOCALE_ERRORS[getLocale()] ?? LOCALE_ERRORS.en;
}

export function installZodLocale(): void {
	z.config({
		localeError: (issue) => activeLocaleError()(issue),
	});
}
